import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '../../../../assets/languages/configs';
import { formatPriceWithMinMax, getValueByLanguage } from '../../../../utils';
import { PRICE_TYPE, STOCK_SALE_STATUS } from '../../../../utils/Constants';
import useLocalizedMoney from '../../../../hooks/useLocalizedMoney';
import useLocalizedTime from '../../../../hooks/useLocalizedTime';
import TextBreaks from '../../../../components/TextBreaks/TextBreaks';
import styles from './styles.module.css';
import { SelectNumber, Stocks, TicketInfo, MultilingualType } from 'types';

type ReservationTicketListProps = {
    reservationTicketList?: Stocks;
    selectNumber: SelectNumber;
    onSelectTicket?: (ticket: Stocks[number]) => void;
    ticketInfo: TicketInfo;
    isSoldOut?: (
        stock: { saleStatus: string; maxReservationCount: number; currentReservationCount: number },
        selectNumber: SelectNumber,
    ) => boolean;
    isEdit?: boolean;
};

const ReservationTicketList = ({
    reservationTicketList = [],
    selectNumber,
    onSelectTicket = () => {},
    ticketInfo,
    isSoldOut = () => false,
    isEdit,
}: ReservationTicketListProps) => {
    const { t } = useTranslation();
    const { language } = i18n;
    const { formatMoney } = useLocalizedMoney();
    const { formatTime } = useLocalizedTime();
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);

    const handleTicketClick = useCallback(
        (reservationTicket: Stocks[number], isSelectable: boolean) => {
            if (isSelectable) {
                onSelectTicket(reservationTicket);
            }
        },
        [onSelectTicket],
    );

    if (reservationTicketList.length === 0) {
        return <div className={styles.noData}>{t('reservation.error-no-ticket')}</div>;
    }

    const unitPriceList = (ticket: TicketInfo | Stocks[number]) => {
        const result: { label?: string; price?: string; number?: number }[] = [];
        const {
            priceAdultMin,
            priceAdultMax,
            priceCustom1Min,
            priceCustom1Max,
            priceCustom2Min,
            priceCustom2Max,
            priceCustom3Min,
            priceCustom3Max,
        } = ticket;
        const priceConfigs = [
            {
                minPrice: priceAdultMin,
                maxPrice: priceAdultMax,
                label: t('ticket-detail.adult'),
                number: selectNumber?.adult,
            },
            {
                minPrice: priceCustom1Min,
                maxPrice: priceCustom1Max,
                label: l(ticketInfo.custom1Name),
                number: selectNumber?.custom1,
            },
            {
                minPrice: priceCustom2Min,
                maxPrice: priceCustom2Max,
                label: l(ticketInfo.custom2Name),
                number: selectNumber?.custom2,
            },
            {
                minPrice: priceCustom3Min,
                maxPrice: priceCustom3Max,
                label: l(ticketInfo.custom3Name),
                number: selectNumber?.custom3,
            },
        ];

        priceConfigs.forEach(({ minPrice, maxPrice, label, number }) => {
            if (label && (number ?? 0) > 0 && ((minPrice ?? 0) > 0 || (maxPrice ?? 0) > 0)) {
                const price = formatPriceWithMinMax({ minPrice, maxPrice }, formatMoney);
                result.push({ label, price, number });
            }
        });

        return result;
    };

    return (
        <>
            {reservationTicketList.map((reservationTicket, index) => {
                const isOnSale = reservationTicket.saleStatus === STOCK_SALE_STATUS.onSale;
                const soldOut = isSoldOut(reservationTicket, selectNumber);
                const isCurrentEditingTicket =
                    ticketInfo?.reservationInfo?.stockId === reservationTicket.stockId && isEdit;
                // boolean値の論理演算子（||）を使用するため、nullish coalescing（??）は適用できない
                // ここではtrueまたはfalseの値を評価しており、null/undefinedのチェックは不要
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                const isSelectable = isCurrentEditingTicket || (isOnSale && !soldOut);

                return (
                    <div
                        key={index}
                        onClick={() => handleTicketClick(reservationTicket, isSelectable)}
                        className={`${styles.wrapper} ${isSelectable ? '' : styles.disabled}`}
                    >
                        <p className={styles.time}>
                            <TextBreaks text={l(reservationTicket?.stockPartitionName)} />
                        </p>

                        <p className={styles.item}>
                            <TextBreaks text={l(reservationTicket?.reservationClassName)} />
                            {!l(reservationTicket?.reservationClassName) ||
                            !l(reservationTicket?.reservationPriceDescription)
                                ? ''
                                : '/ '}

                            <TextBreaks text={l(reservationTicket?.reservationPriceDescription)} />
                        </p>
                        <div className={styles.flexRows}>
                            {ticketInfo?.isDisplayReserveOnTime !== false && (
                                <p className={styles.time}>{formatTime(reservationTicket.reserveOn, 'LT')}</p>
                            )}
                            {ticketInfo.isDisplayRemainingStock && isOnSale && !soldOut && (
                                <p className={styles.item}>
                                    {t('reservation.residue')}:{' '}
                                    {reservationTicket.maxReservationCount - reservationTicket.currentReservationCount}
                                </p>
                            )}
                            {reservationTicket.saleStatus === STOCK_SALE_STATUS.deadlinePassed && (
                                <p className={styles.item}>{t('reservation.deadline-passed')}</p>
                            )}
                            {soldOut && <p className={styles.item}>{t('reservation.sold-out')}</p>}
                            {reservationTicket.saleStatus === STOCK_SALE_STATUS.suspended && (
                                <p className={styles.item}>{t('reservation.suspended')}</p>
                            )}
                            {/* <p className={styles.price}>
                                {formatMoney(
                                    calcTotalPrice(selectNumber, {
                                        adult: reservationTicket?.priceAdult,
                                        custom1: reservationTicket?.priceCustom1 || 0,
                                        custom2: reservationTicket?.priceCustom2 || 0,
                                        custom3: reservationTicket?.priceCustom3 || 0,
                                    }),
                                ) + currencyUnit('JPY')}
                            </p> */}
                        </div>
                        {ticketInfo.priceType === PRICE_TYPE.ADDITION &&
                            unitPriceList(reservationTicket)?.length > 0 &&
                            isOnSale &&
                            !soldOut && (
                                <>
                                    <div className={styles.vertLine}></div>
                                    <div className={styles.ticketPrice}>
                                        {unitPriceList(reservationTicket).map((item, index) => (
                                            <span key={index} style={{ marginRight: 10 }}>
                                                {item.label} / {item.price} x {item.number}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            )}

                        {l(reservationTicket.stockPartitionDescription) && (
                            <>
                                <div className={styles.vertLine}></div>
                                <p className={styles.item}>
                                    <TextBreaks text={l(reservationTicket?.stockPartitionDescription)} />
                                </p>
                                <div className={styles.onlySpace}></div>
                            </>
                        )}
                    </div>
                );
            })}
        </>
    );
};

export default memo(ReservationTicketList);
